import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AlertCard from '../AlertCard';
import { Pagination } from 'antd';
import AlertMap from '../AlertMap';
import { analysisService, AlertData } from '../../../../api/services/analysisService';
import type { Basket } from '../../../../types/basket';
import { referenceDataSyncService } from '../../../../services/referenceDataSyncService';
import FormItem from 'antd/es/form/FormItem';
import { Select, DatePicker } from 'antd';
import { normalize } from '../../../../utils/normalizeWords';
import dayjs from 'dayjs';
import { PermissionEnum } from '../../../../enums/PermissionEnum';
import { usePermissions } from '../../../../hooks/usePermissions';

const PriceAlertsTab = () => {
  const { t } = useTranslation(['advancedAnalysis']);
  const { hasPermission } = usePermissions();
  const canViewAlerts = hasPermission(PermissionEnum.VIEW_ALERTS);
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(12);

  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [selectedBasket, setSelectedBasket] = useState<number[]>([]);
  const [presentations, setPresentations] = useState<any>([]);
  const [selectedPresentations, setSelectedPresentations] = useState<string[]>([]);
  const [metric, setMetric] = useState<string>();
  const today = dayjs();
  const oneMonthAgo = today.subtract(1, 'month');
  const [selectedDateRange, setSelectedDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([
    oneMonthAgo,
    today,
  ]);
  const basketItems = [
    ...baskets.map((basket) => ({
      label: basket.name,
      value: basket.id,
    })),
  ];

  const fetchAlerts = async (page: number = currentPage, limit: number = pageSize) => {
    if (!canViewAlerts) {
      return;
    }

    try {
      setLoading(true);
      const response = await analysisService.getAlertList({
        page,
        limit,
        basketIds: selectedBasket,
        productRefIds: selectedPresentations,
        ...(selectedDateRange
          ? { dateRange: selectedDateRange.map((date) => date.valueOf()) as [number, number] }
          : {}),
        metric: metric,
      });
      setAlerts(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewAlerts) {
      fetchAlerts();
    }
  }, [
    canViewAlerts,
    currentPage,
    pageSize,
    selectedBasket,
    selectedPresentations,
    metric,
    selectedDateRange,
  ]);

  useEffect(() => {
    const loadData = async () => {
      if (!canViewAlerts) {
        return;
      }

      try {
        const [basketsData] = await Promise.all([referenceDataSyncService.getBaskets()]);

        setBaskets(basketsData as unknown as Basket[]);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [canViewAlerts]);

  const loadPresentationsForBasket = useCallback(async () => {
    if (!canViewAlerts) {
      return;
    }

    try {
      const filteredPresentations =
        await referenceDataSyncService.getPresentationsForFilters(selectedBasket);

      const presentationOptionArr = filteredPresentations.map((presentation: any) => ({
        label: presentation.name,
        value: presentation.refId,
        id: presentation.refId,
      }));

      setPresentations(presentationOptionArr);
    } catch (error) {
      console.error('Error loading presentations for selection:', error);
      setPresentations([]);
    }
  }, [canViewAlerts, selectedBasket]);

  useEffect(() => {
    loadPresentationsForBasket();
  }, [loadPresentationsForBasket]);

  if (!canViewAlerts) {
    return null;
  }

  const handleBasketChange = (value: any) => {
    setSelectedBasket(value);
    setSelectedPresentations([]);
  };

  return (
    <div className="mb-20 mt-8">
      <div className="mb-8 mt-4 flex flex-col items-center gap-6 lg:flex-row">
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('basket')}</span>}
        >
          <Select
            value={selectedBasket}
            options={basketItems}
            placeholder={t('basketPlaceholder')}
            showSearch
            mode="multiple"
            onChange={(val) => {
              handleBasketChange(val);
            }}
            filterOption={(input, option) =>
              normalize(option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(normalize(input.toLowerCase()))
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
          <div style={{ display: 'flex', marginBottom: '0px' }}>
            {selectedBasket?.length > 0 && (
              <a
                className="ml-auto"
                onClick={() => {
                  handleBasketChange([]);
                }}
              >
                {t('deselectAll')}
              </a>
            )}
          </div>
        </FormItem>
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('presentations')}</span>}
        >
          <Select
            value={selectedPresentations}
            options={presentations}
            placeholder={t('presentationsPlaceholder')}
            onClear={() => setSelectedPresentations([])}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            onChange={(val) => {
              setSelectedPresentations(val);
            }}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
          <div style={{ display: 'flex', marginBottom: '0px' }}>
            {selectedPresentations?.length > 0 && (
              <a
                className="ml-auto"
                onClick={() => {
                  setSelectedPresentations([]);
                }}
              >
                {t('deselectAll')}
              </a>
            )}
          </div>
        </FormItem>{' '}
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('metric')}</span>}
        >
          <Select
            value={metric}
            options={[
              { label: t('manual'), value: 'manual' },
              { label: t('average'), value: 'average' },
            ]}
            placeholder={t('metricPlaceholder')}
            allowClear
            onChange={(val) => {
              setMetric(val);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="relative col-span-2 mb-0 w-full 3xl:col-span-1"
          layout="vertical"
          label={
            <span className="text-sm font-medium text-gray-700">{t('triggeredDateRange')}</span>
          }
        >
          <DatePicker.RangePicker
            className="mt-2 rounded-lg px-4 py-2 md:mt-0"
            value={selectedDateRange}
            onChange={(dates) => setSelectedDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
            format="YYYY-MM-DD"
            placeholder={[t('from'), t('to')]}
            disabledDate={(current) => {
              return current > dayjs().add(1, 'day').endOf('day');
            }}
            allowClear={false}
          />
        </FormItem>
      </div>
      <div className="rounded-lg border border-border-color p-4">
        <h4 className="text-2xl font-semibold">{t('pricesAlertsTitle')}</h4>
        <p className="text-font-color">{t('citizenPricesAlertsDescription')}</p>

        <div className="mb-3 mt-4 grid min-w-0 auto-rows-fr grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {loading ? (
            <div className="col-span-full py-8 text-center">{t('loadingAlerts')}</div>
          ) : alerts.length === 0 ? (
            <div className="col-span-full py-8 text-center">{t('noAlertsFound')}</div>
          ) : (
            alerts.map((alert: AlertData) => (
              <div key={alert.id} className="flex h-full w-full min-w-0">
                <AlertCard
                  title={`${alert.productName} (${alert.productPresentation})`}
                  location={
                    alert.location !== 'Location not available'
                      ? alert.location
                      : t('locationNotAvailable')
                  }
                  basketName={alert.basketName || t('unknownBasket')}
                  reportedPrice={alert.lastReportedPrice || 0}
                  triggeredAt={alert.triggeredAt}
                  reason={t('triggeredInstancesCount', {
                    price: alert.alertThreshold,
                    product: alert.productName,
                    instanceCount: alert.instanceCount,
                  })}
                  alertId={alert.id}
                  alertConfigId={alert.alertConfigId}
                  productId={alert.productId}
                  alertThreshold={alert.alertThreshold}
                  alertThresholdValue={alert.alertThresholdValue}
                  alertCondition={alert.alertCondition}
                />
              </div>
            ))
          )}
        </div>

        {total > 0 && (
          <div className="mt-6 flex justify-center">
            <Pagination
              current={currentPage}
              total={total}
              pageSize={pageSize}
              showSizeChanger
              pageSizeOptions={['12', '24', '36', '48']}
              showQuickJumper
              showTotal={(total, range) =>
                t('alertPaginationRange', { range0: range[0], range1: range[1], total: total })
              }
              onChange={(page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                }
              }}
              onShowSizeChange={(_, size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              locale={{
                page: t('page'),
                items_per_page: `/ ${t('page')}`,
              }}
            />
          </div>
        )}
      </div>

      <section className="mt-20">
        <h5 className="text-2xl font-semibold text-[#0a0a0a]">{t('alertLocations')}</h5>
        <div className="mt-8">
          <AlertMap t={t} />
        </div>
      </section>
    </div>
  );
};

export default PriceAlertsTab;
